import axios from 'axios'
import axiosWrapper, { InterceptorOptions } from './axios-wrapper'
import {
  setUserLoading,
  setUserLoggedIn,
  setUserLoggedOut,
} from '../actions/user-actions'
import store from '../store'
import { dispatchEvent } from '../CloudWatch-init'

const api = () =>
  axiosWrapper.create(
    { baseURL: '/' },
    InterceptorOptions.defaultErrorInterceptor,
  )

// Sign-in and password reset requests. These go straight through axios: the
// sign-in page shows their errors itself, a 401 here is a rejected password
// rather than an expired session to redirect from, and a credential request
// must never be retried automatically.
const signInApi = () => axios.create({ baseURL: '/' })

export function getUser() {
  store.dispatch(setUserLoading())
  return api()
    .get(`/tracer/userinfo`)
    .then((response) => {
      const userInfo = response.data
      store.dispatch(setUserLoggedIn(userInfo))
    })
    .catch((error) => {
      store.dispatch(setUserLoggedOut())
      console.log(error)
    })
}

export function getUserInfo() {
  return api()
    .get(`/tracer/userinfo`)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error)
    })
}

export function login(options = {}) {
  const queryParameters = new URLSearchParams(window.location.search)
  const loginParams = new URLSearchParams({
    redirect_url:
      options.redirect_url ?? queryParameters.get('redirect_url') ?? '/',
  })
  const identityProvider =
    options.identity_provider ?? queryParameters.get('identity_provider')
  if (identityProvider) {
    loginParams.set('identity_provider', identityProvider)
  }

  return api()
    .get(`/tracer/login_url?${loginParams.toString()}`)
    .then((response) => {
      if (response.data && response.data.location) {
        window.location = response.data.location
      } else {
        console.log(
          'Error: Failed to retrieve a valid login URL from the server.',
        )
      }
    })
    .catch((error) => {
      console.log(error)
    })
}

export function getLoginConfig() {
  return api()
    .get('/tracer/login_config')
    .then((response) => response.data)
}

export function nativeLogin(payload) {
  return signInApi()
    .post('/tracer/login/native', payload)
    .then((response) => response.data)
}

export function respondToLoginChallenge(payload) {
  return signInApi()
    .post('/tracer/login/challenge', payload)
    .then((response) => response.data)
}

export function requestPasswordReset(payload) {
  return signInApi()
    .post('/tracer/login/forgot_password', payload)
    .then((response) => response.data)
}

export function confirmPasswordReset(payload) {
  return signInApi()
    .post('/tracer/login/confirm_forgot_password', payload)
    .then((response) => response.data)
}

export function logout(redirectUrl) {
  // Try to get user info for logging
  let userName = null
  let userID = null
  let plant = null
  try {
    const state = store.getState()
    const userData = state.user && state.user.data
    // get plant from localstorage if available
    plant = localStorage.getItem('user-plant') || null
    if (plant) {
      plant = JSON.parse(plant)
    }
    if (userData) {
      userName = userData.username
      userID = userData.client_id
      if (!plant) {
        plant =
          userData.plants && userData.plants[0] ? userData.plants[0] : null
      }
    }
  } catch (e) {}

  // Send log event and wait for it to complete (but not block forever)
  const logPromise =
    dispatchEvent && typeof dispatchEvent === 'function'
      ? Promise.resolve(
          dispatchEvent('USER_LOG_OFF', {
            userName,
            userID,
            timestamp: Date.now(),
            plant,
          }),
        )
      : Promise.resolve()

  // Wait up to 500ms for the log event, then redirect
  return logPromise
    .then(() => {
      return new Promise((resolve) => setTimeout(resolve, 200))
    })
    .finally(() => {
      if (!redirectUrl) {
        const queryParameters = new URLSearchParams(window.location.search)
        redirectUrl = queryParameters.has('redirect_url')
          ? queryParameters.get('redirect_url')
          : '/'
      }
      api()
        .get(
          `/tracer/logout_url?redirect_url=${encodeURIComponent(redirectUrl)}`,
        )
        .then((response) => {
          if (response.data && response.data.location) {
            window.location = response.data.location
          } else {
            console.log(
              'Error: Failed to retrieve a valid logout URL from the server.',
            )
          }
        })
        .catch((error) => {
          console.log(error)
        })
    })
}

export function auth0callback() {
  return api()
    .get(`/tracer/auth0callback${window.location.search}`, {
      withCredentials: true,
    })
    .then((response) => {
      if (response.data && response.data.location) {
        window.location = response.data.location
      } else {
        console.log(
          'Error: Failed to retrieve a valid Auth0 callback redirect URL from the server.',
        )
        window.location = '/'
      }
    })
    .catch((error) => {
      console.log(error)
    })
}
